import { Button, ConfigProvider, DatePicker, Form, Input, InputNumber, message, Modal, Select, Tag } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { PaymentWithAccountantPayload, WaterInvoiceModel } from "@/core/models/water-invoice.model";
import { PaymentWithAccountantMethod } from "@/core/enums/global.enums";
import { paymentWithAccountantMethodPipe } from "@/core/pipes/pay-accountant-method.pipe";
import { currencyFormatter, formatCurrencyPipeText } from "@/core/pipes/format-currency.pipe";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { toApiTimestamp, DEF_DATETIME_FORMAT } from "@/app/home/utils/global.utils";
import { UpdatePaymentDateModal } from "./UpdatePaymentDateModal";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: WaterInvoiceModel,
    onSuccess?: () => void;
}

const INPUT_WIDTH = 450;

export const PaymentWithAccountantModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data, onSuccess}) => {
    const [form] = Form.useForm<PaymentWithAccountantPayload>();
    const [loading, setLoading] = useState<boolean>(false);
    const [isTotalDisabled, setTotalDisabled] = useState<boolean>(false);
    const [isUpdatePaymentDateModalOpen, setIsUpdatePaymentDateModalOpen] = useState<boolean>(false);

    // Attach hooks to handle data changes
    const handleDataChange = useCallback(async () => {
        form.setFieldValue("id", data.id);
        form.setFieldValue("total", 0);
        form.setFieldValue("actualReceivedDate", null);
    }, [form, data.id])
    useEffect(() => {
        handleDataChange();
    }, [data, handleDataChange])


    // Initialize select options data
    const init = async () => { }

    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            const result = await waterInvoiceRepo.payWithAccountant({...formData, id: data.id, actualReceivedDate: toApiTimestamp(formData.actualReceivedDate)});
            if (result === true) {
                message.success({
                    content: "Gạch nợ thành công!"
                })
                setIsModalOpen(false);
                onSuccess?.();
            } else {
                message.error({
                    content: typeof result === "string"?result:"Thanh toán gạch nợ thất bại!"
                })
            }
        
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      setIsModalOpen(false);
    };
    const handleMethodChange = (value: string) => {
        if (value === PaymentWithAccountantMethod.Debtminusdeposit) {
            setTotalDisabled(true);
            form.setFieldValue("total", data.total);
        } else {
            setTotalDisabled(false);
            form.setFieldValue("total", 0);
        }
    }

    const handleUpdatePaymentDate = () => {
        setIsUpdatePaymentDateModalOpen(true);
    }

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal open={isModalOpen} onCancel={handleCancel} width={520} centered
            styles={{
                mask: {
                    backdropFilter: 'blur(2px)',
                    backgroundColor: 'var(--primary-mask)'
                }
            }}
            footer={!data.paymentDate ? [
                <div key={1} className="w-full flex flex-row justify-end px-1.5">
                    <Button key="submit" type="primary" loading={loading}
                        style={{ 
                            background: "var(--primary-color)", 
                            paddingRight: 48, 
                            paddingLeft: 48, 
                            fontSize: 18,
                            fontWeight: 600,
                            paddingTop: 18, 
                            paddingBottom: 18,
                            }}
                        onClick={handleSave}
                    >
                        THANH TOÁN
                    </Button>
                </div>,
            ] : null}>
                <div className="flex flex-row w-full items-start justify-between select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">
                        {data.paymentDate ? "THÔNG TIN THANH TOÁN" : "GẠCH NỢ KẾ TOÁN"}
                    </p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    {data.paymentDate ? (
                        // Hiển thị thông tin thanh toán khi đã thanh toán
                        <div className="flex flex-col items-start w-full px-4">
                            <div className="text-lg pb-4 w-full">
                                <div className="mb-4">
                                    <p><b>Mã hóa đơn:</b> <span style={{color: "var(--primary-color)"}}>{data.codeString}</span></p>
                                    <p><b>Hóa đơn tháng:</b> <span style={{color: "var(--primary-color)"}}>{data.month}/{data.year}</span></p>
                                    <p><b>Mã khách hàng:</b> {data.waterUser?.code}</p>
                                    <p><b>Tên khách hàng:</b> {data.waterUser?.name}</p>
                                    <p><b>Giá trị hóa đơn:</b> {formatCurrencyPipeText(data.total)}</p>
                                </div>
                                
                                <div className="border-t border-gray-200 pt-4 mb-4">
                                    <p><b>Trạng thái thanh toán:</b></p>
                                    <Tag color="green" className="mb-3">Đã thanh toán</Tag>
                                    
                                    <p><b>Loại hình thanh toán:</b></p>
                                    <Tag color="blue" className="mb-3">
                                        {data.paymentMethod ? paymentWithAccountantMethodPipe(data.paymentMethod) : data.paymentMethod}
                                    </Tag>
                                    
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <p><b>Ngày thanh toán:</b></p>
                                            <span>{data.paymentDate ? dayjsPipe(data.paymentDate)?.format(DEF_DATETIME_FORMAT) : "Chưa có"}</span>
                                        </div>
                                        <Button 
                                            type="default"
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={handleUpdatePaymentDate}
                                            style={{
                                                borderColor: "var(--primary-color)",
                                                color: "var(--primary-color)",
                                                fontWeight: 600,
                                                paddingLeft: 12,
                                                paddingRight: 12,
                                                height: 28
                                            }}
                                        >
                                            Sửa ngày
                                        </Button>
                                    </div>
                                    
                                    {data.debtCollector && (
                                        <p><b>Người thu nợ:</b> {data.debtCollector}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Hiển thị form thanh toán khi chưa thanh toán
                        <Form<PaymentWithAccountantPayload>
                            name="basic"
                            className="flex flex-col items-end"
                            form={form}
                            layout="vertical"
                        >
                            <div>
                                <div className="text-lg pb-2">
                                    <p><b>Mã hóa đơn: <span style={{color: "var(--primary-color)"}}>{data.codeString}</span></b></p>
                                    <p><b>Hóa đơn tháng: <span style={{color: "var(--primary-color)"}}>{data.month}/{data.year}</span></b></p>
                                    <p><b>Mã khách hàng: {data.waterUser?.code}</b></p>
                                    <p><b>Tên khách hàng: </b>{data.waterUser?.name}</p>
                                    <p><b>Giá trị hóa đơn: </b>{formatCurrencyPipeText(data.total)}</p>
                                </div>
                                <Form.Item<PaymentWithAccountantPayload>
                                    label="Loại hình Thanh toán"
                                    name="paymentMethod"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Select showSearch placeholder="Chọn Hình thức thanh toán" style={{width: INPUT_WIDTH}} onChange={handleMethodChange}>
                                    {Object.keys(PaymentWithAccountantMethod).map(opt => <Select.Option key={opt} value={opt}>{paymentWithAccountantMethodPipe(opt)}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                                <Form.Item<PaymentWithAccountantPayload>
                                    label="Số tiền thanh toán"
                                    name="total"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <InputNumber controls={false} formatter={currencyFormatter} placeholder="Nhập Số tiền" style={{width: INPUT_WIDTH}} disabled={isTotalDisabled}/>
                                </Form.Item>
                                <Form.Item<PaymentWithAccountantPayload>
                                    label="Ngày nhận tiền thực tế"
                                    name="actualReceivedDate"
                                    getValueProps={(i)=>({value: dayjsPipe(i)})}
                                >
                                    <DatePicker format={DEF_DATETIME_FORMAT} showTime style={{width: INPUT_WIDTH}}></DatePicker>
                                </Form.Item>
                                <Form.Item<PaymentWithAccountantPayload>
                                    label="Thông tin giao dịch"
                                    name="information"
                                >
                                    <Input.TextArea style={{width: INPUT_WIDTH, height: 202, resize: "none"}}></Input.TextArea>
                                </Form.Item>
                            </div>
                        </Form>
                    )}
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>

            {/* Update Payment Date Modal */}
            <UpdatePaymentDateModal
                isModalOpen={isUpdatePaymentDateModalOpen}
                setIsModalOpen={setIsUpdatePaymentDateModalOpen}
                data={data}
                onSuccess={() => {
                    onSuccess?.();
                }}
            />
        </ConfigProvider>
    )
}